/**
 * Stale-chunk recovery.
 *
 * We ship a hashed, lazily-loaded bundle: routes and heavy features (jsPDF,
 * the training-manual exporter, etc.) are pulled in with dynamic `import()`.
 * When a new build deploys, the old hashed asset URLs a still-open tab
 * remembers no longer exist. Our Netlify SPA fallback (`/* -> /index.html
 * 200`, see public/_redirects) then answers those `/assets/*.js` requests
 * with `index.html` — HTML, status 200 — so the browser tries to execute
 * markup as an ES module and the import rejects:
 *
 *   TypeError: Failed to fetch dynamically imported module: …
 *   SyntaxError: Unexpected token '<'                      (HTML-as-JS)
 *
 * Any feature that catches its own import error (like the training-manual
 * export) then shows a generic "please try again" — but retrying re-requests
 * the same dead URL, so it stays broken until the user hard-reloads. This
 * module turns that dead end into a one-shot reload that picks up the fresh
 * asset manifest.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from "react";

const RELOAD_FLAG = "da:chunk-reload-at";
// Only auto-reload once per short window. If the reloaded build STILL can't
// load the chunk (genuinely broken deploy, offline), we stop retrying and let
// the error surface instead of thrashing the page in a reload loop.
const COOLDOWN_MS = 15_000;

/** Does this error look like a failed / stale lazy-chunk fetch? */
export function isChunkLoadError(err: unknown): boolean {
  const e = err as { message?: unknown; name?: unknown } | null | undefined;
  if (e?.name === "ChunkLoadError") return true;
  const msg = typeof e?.message === "string" ? e.message : String(err ?? "");
  return (
    /failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /importing a module script failed/i.test(msg) || // Safari
    /unexpected token '<'/i.test(msg) || // HTML served in place of JS
    /'?text\/html'? is not a valid javascript mime type/i.test(msg) ||
    // A stale chunk that RESOLVES to a module with no usable `default` export:
    // React.lazy then reads `.default` off it and throws one of these. The
    // messages read generic, but prod error_logs show they are overwhelmingly
    // post-deploy stale chunks (the single biggest source of the "Something
    // went wrong" flash — ~160 hits/10d from the React.lazy initializer on
    // route navigation), not real app bugs. A false positive costs one reload:
    // reloadForFreshChunks's 15s cooldown then lets the real error surface, so
    // a genuine `.default` bug still reaches the boundary on the second render.
    /reading 'default'/i.test(msg) || // Chrome: Cannot read properties of undefined (reading 'default')
    /_result\.default/i.test(msg) || // Safari: undefined is not an object (evaluating 'e._result.default')
    /resolved without a default export/i.test(msg) || // our own lazyWithReload guard (stale build)
    // Safari's terse label for a failed fetch — including a failed dynamic
    // import(). App data fetches are awaited/handled and don't surface here;
    // an unhandled "Load failed" reaching the boundary or unhandledrejection
    // is a chunk fetch (128 hits/10d, bursts of parallel imports on /limited).
    /^load failed$/i.test(msg.trim())
  );
}

/**
 * Reload once to fetch the freshly-deployed assets. Returns true if it
 * triggered a reload, false if we reloaded too recently (cooldown) and are
 * deliberately letting the error stand.
 */
export function reloadForFreshChunks(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0);
    if (last && Date.now() - last < COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage can be unavailable (private mode, locked-down kiosk).
    // Fall through and reload anyway — a rare double-reload beats a wedged app.
  }
  window.location.reload();
  return true;
}

/**
 * Drop-in replacement for `React.lazy` that recovers from stale-chunk failures.
 *
 * A route chunk from an older deploy fails to load in two different shapes, and
 * the ErrorBoundary fix in #903 only taught the boundary about the first:
 *
 *   1. The dynamic `import()` REJECTS — "Failed to fetch dynamically imported
 *      module" / a text/html MIME error. React.lazy re-throws that reason on the
 *      next render, and `isChunkLoadError` already recognizes it, so the
 *      boundary shows the branded loader and reloads.
 *   2. The `import()` RESOLVES to a module with no usable `default` export (the
 *      Netlify SPA fallback answered `/assets/*.js` with index.html, or a
 *      half-evaluated stale chunk). React.lazy then reads `.default` off
 *      `undefined` and throws the GENERIC "Cannot read properties of undefined
 *      (reading 'default')" (Safari: "...evaluating 'e._result.default'"). That
 *      message is far too generic to safely match in the global boundary, so it
 *      fell through to the full-screen "Something went wrong" page — and prod
 *      error_logs show it was the single biggest crash users still hit after
 *      #903 (75 hits in 5 days, across /messages, /communications, /my-tickets…).
 *
 * Catching the failure HERE, at the import factory, removes the ambiguity: any
 * failure loading a route chunk is by construction a stale-chunk problem, so we
 * reload once for the fresh manifest instead of string-matching a bare
 * TypeError after React has already thrown it into the boundary.
 */
export function lazyWithReload<T extends ComponentType<Record<string, unknown>>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().then(
      (mod) => {
        // A stale chunk can resolve WITHOUT throwing yet carry no component
        // (e.g. index.html parsed as an empty module, or a named-export
        // transform whose export came back undefined). React.lazy would read
        // `.default` off that and throw the generic TypeError above — so treat a
        // missing default exactly like a failed import and reload instead.
        if (!mod || mod.default == null) {
          return handleChunkFailure<T>(
            new Error("Route chunk resolved without a default export (stale build)"),
          );
        }
        return mod;
      },
      (err) => handleChunkFailure<T>(err),
    ),
  );
}

/**
 * `lazyWithReload` for a NAMED export that takes props:
 *
 *   const Dialog = lazyNamedWithReload(() => import("./Dialog").then((m) => m.Dialog));
 *
 * lazyWithReload's `ComponentType<Record<string, unknown>>` bound rejects
 * components with required props under type-checking, which is why most
 * call sites wrapped named exports in `{ default: m.X }` by hand and lost the
 * props type. This keeps the component's own props type for callers, and a
 * named export that comes back undefined (stale chunk) still hits the
 * missing-default guard above and reloads.
 */
export function lazyNamedWithReload<P>(
  factory: () => Promise<ComponentType<P>>,
): LazyExoticComponent<ComponentType<P>> {
  return lazyWithReload(() =>
    factory().then((C) => ({ default: C as unknown as ComponentType<Record<string, unknown>> })),
  ) as unknown as LazyExoticComponent<ComponentType<P>>;
}

function handleChunkFailure<T>(err: unknown): Promise<{ default: T }> {
  const reloading = reloadForFreshChunks();
  if (reloading) {
    // A reload is in flight. Keep React suspended on the Suspense fallback (the
    // BrandedLoader) — resolving or rejecting now would flash UI in the frames
    // before window.location.reload() tears the page down.
    return new Promise<{ default: T }>(() => {});
  }
  // Cooldown hit: we already reloaded moments ago and the chunk STILL won't load
  // (genuinely broken deploy / offline). Surface the error so the ErrorBoundary
  // shows its actionable page instead of spinning on the loader forever.
  return Promise.reject(err);
}

/**
 * Wire the app-wide guard. Call once at startup, before render.
 *
 * `vite:preloadError` is Vite's first-class signal for a lazy import that
 * failed to load — it fires even when the importing code catches the
 * rejection itself. We also watch `unhandledrejection` for the odd chunk
 * error that arrives without a preload event (e.g. some Safari builds).
 */
export function installChunkReloadGuard(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("vite:preloadError", (event) => {
    // Don't let it bubble to the global error monitor as a hard crash — this
    // is an expected, recoverable "you're on an old build" condition.
    event.preventDefault();
    reloadForFreshChunks();
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      reloadForFreshChunks();
    }
  });
}

/**
 * Proactive stale-build detection — the durable counterpart to the reactive
 * recovery above.
 *
 * Everything above only fires AFTER a stale lazy import has already failed, and
 * only if THIS tab's `isChunkLoadError` recognizes the specific failure shape.
 * That's a losing game for a long-idle tab: the classifier lives in the very
 * bundle that's out of date, so each new failure shape flashes the scary
 * "Something went wrong" page for a whole deploy generation before a fix can
 * reach the tabs that need it. (Prod error_logs bore this out — the flashes
 * were all from days-old bundles still open in idle tabs; #923 introduced a
 * "resolved without a default export" throw that the SAME build couldn't yet
 * match until #975 shipped the regex a day later.)
 *
 * This flips the model from message-matching to VERSION-matching, which no new
 * failure shape can defeat: when the tab regains visibility (exactly the "came
 * back to it after a while" moment the user hits), compare the entry-bundle hash
 * this tab is running against the one the server serves now. A mismatch means a
 * deploy happened while we were idle, so reload for the fresh manifest BEFORE
 * the user navigates into now-dead lazy chunks — a clean branded-loader reload
 * instead of an error page.
 *
 * The reload is DEFERRED to the next route change, never taken on the
 * visibility/focus event itself. Regaining focus is not only "came back after
 * a while": opening a file picker blurs the window and closing it refocuses
 * it. Reloading right there threw away whatever the user was mid-way through —
 * Ryan Green (Fresh Fill marketing, 2026-09-23) attached a screenshot to a
 * Team Comms post and again to a bug report, and both times the page reset
 * under him because a deploy had landed minutes earlier. A route change is the
 * moment the user is leaving the current screen anyway, and it is also the
 * moment the dead lazy chunks would actually be requested, so nothing is lost
 * by waiting for it. AppContent calls `reloadIfStaleBuildPending()` on each
 * pathname change.
 *
 * Web only. Native (Capacitor) ships its assets in the app bundle and updates
 * via Capgo OTA, so there's no Netlify index.html to diff — the caller gates
 * this behind `!Capacitor.isNativePlatform()`.
 */

/** Pull the hashed entry-bundle path out of an index.html document. */
export function parseEntryAsset(html: string): string | null {
  // Vite emits exactly one entry chunk named `index-<hash>.js` (plus its
  // modulepreload link) — any other chunk carries its source name. So the first
  // `/assets/index-<hash>.js` occurrence is the entry regardless of tag order.
  const m = html.match(/\/assets\/index-[A-Za-z0-9_-]+\.js/);
  return m ? m[0] : null;
}

/** True only when both hashes are known AND differ (a newer build is live). */
export function isNewerBuildAvailable(
  running: string | null,
  deployed: string | null,
): boolean {
  if (!running || !deployed) return false;
  return running !== deployed;
}

/** The entry-bundle URL this tab booted from, read off the module script tag. */
function runningEntryUrl(): string | null {
  if (typeof document === "undefined") return null;
  const el = document.querySelector<HTMLScriptElement>(
    'script[type="module"][src*="/assets/index-"]',
  );
  const src = el?.getAttribute("src") ?? null;
  return src ? parseEntryAsset(src) : null;
}

/** Fetch the freshly-served index.html and read its entry-bundle path. */
async function deployedEntryUrl(): Promise<string | null> {
  try {
    // no-store so we compare against what the CDN serves NOW, not a cached copy.
    const res = await fetch("/index.html", { cache: "no-store" });
    if (!res.ok) return null;
    return parseEntryAsset(await res.text());
  } catch {
    // Offline / transient — treat as "can't tell" and leave the tab alone.
    return null;
  }
}

// Don't hammer the origin: at most one index.html check per this window even if
// the tab is focused/blurred rapidly.
const VISIBILITY_CHECK_THROTTLE_MS = 30_000;

// Set when a newer deploy has been detected; consumed on the next route change.
let staleBuildPending = false;

/** True when a newer deploy is live and the next navigation should reload. */
export function isStaleBuildReloadPending(): boolean {
  return staleBuildPending;
}

/**
 * Call on each route change. If a newer build was detected while the user sat
 * on the previous screen, reload now — the URL has already moved to the new
 * route, so the reload lands the user exactly where they were headed, on fresh
 * assets. Returns true if it reloaded.
 */
export function reloadIfStaleBuildPending(): boolean {
  if (!staleBuildPending) return false;
  // reloadForFreshChunks de-dupes via the same 15s sessionStorage cooldown the
  // reactive path uses, so a proactive reload and a chunk-error reload can't
  // stack into a loop. On a cooldown refusal keep the flag set so a later
  // navigation still picks up the new build.
  const reloaded = reloadForFreshChunks();
  if (reloaded) staleBuildPending = false;
  return reloaded;
}

/** Test-only: reset module state between cases. */
export function __resetStaleBuildWatchForTests(): void {
  staleBuildPending = false;
}

export function installStaleBuildWatch(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const running = runningEntryUrl();
  // No entry tag to fingerprint against (unexpected) — bail rather than guess.
  if (!running) return;

  let lastCheck = 0;
  let checking = false;

  const check = async () => {
    if (document.visibilityState !== "visible") return;
    if (checking) return;
    const now = Date.now();
    if (now - lastCheck < VISIBILITY_CHECK_THROTTLE_MS) return;
    lastCheck = now;
    checking = true;
    try {
      const deployed = await deployedEntryUrl();
      if (isNewerBuildAvailable(running, deployed)) {
        // Only flag it — see the block comment above for why reloading here
        // (e.g. right after a file picker closes) destroys unsaved work.
        staleBuildPending = true;
      }
    } finally {
      checking = false;
    }
  };

  document.addEventListener("visibilitychange", () => void check());
  window.addEventListener("focus", () => void check());
}
